import { AbstractControl } from '@angular/forms';
import { BaseFormComponent, DatasetFormData } from 'src/app/@shared/form/base-form.component';
import { Component, Input, inject } from '@angular/core';
import { FormFieldValue } from 'src/app/@shared/form/form.types';
import { TopicModel } from 'src/app/topic/@shared/topic.model';
import { TopicService } from 'src/app/topic/@shared/topic.service';
import { isEmpty } from 'src/app/@shared/app-helper';

const IS_RARE_DISEASE_FIELD_ID = 'isRareDisease';
const ORPHANET_FIELD_ID_KEYWORD = 'orphanet';

@Component({
  selector: 'topic-form',
  standalone: false,
  styleUrls: ['./topic-form.component.scss'],
  templateUrl: './topic-form.component.html',
})
export class TopicFormComponent extends BaseFormComponent<TopicModel> {
  @Input() public isEditMode: boolean;

  protected topicService = inject(TopicService);

  private orphanetFieldIds: string[] = [];

  public onValueUpdated(currentValue: FormFieldValue | FormFieldValue[], currentFieldId: string): void {
    if (!this.orphanetFieldIds.includes(currentFieldId)) {
      return;
    }

    this.evaluateOrphanetFieldsValues(currentValue);
  }

  protected onDataReceived(data: TopicModel): void {
    this.datasetData = {
      ...data,
    } as TopicModel;

    this.prepareForm();
    this.patchFormByDataset(this.datasetData);

    // since dynamic fields are patched more complicatedly, they are not set in the editForm just yet
    // --> trigger a new tick
    requestAnimationFrame(() => {
      // check orphanet fields for entries on init after the form is patched (null is just the placeholder here)
      this.evaluateOrphanetFieldsValues(null);
    });
  }

  protected async handleRequest(formData: DatasetFormData): Promise<boolean> {
    if (this.isEditMode) {
      return !!await this.topicService.patch(this.datasetData.id, formData);
    }

    return !!await this.topicService.create(formData, 'topics');
  }

  protected prepareForm(): void {
    super.prepareForm();

    this.orphanetFieldIds = Object.keys(this.editForm.controls)
      .filter((key) => {
        return key.toLowerCase().includes(ORPHANET_FIELD_ID_KEYWORD);
      });
  }

  // Check orphanet fields for values and set the isRareDisease toggle accordingly.
  private evaluateOrphanetFieldsValues(currentValue: FormFieldValue | FormFieldValue[]): void {
    if (!isEmpty(currentValue)) {
      this.setIsRareDiseaseValue(true);

      return;
    }

    const anyFieldHasValue = this.orphanetFieldIds.some((fieldId) => {
      const control = this.editForm.get(fieldId);

      if (!control) {
        console.warn('Could not evaluate orphanet field correctly since the control is missing', control);

        return false;
      }

      const { value: controlValue } = control;

      if (!Array.isArray(controlValue)) {
        return !!controlValue;
      }

      return this.dynamicFieldHasValue(controlValue);
    });

    this.setIsRareDiseaseValue(anyFieldHasValue);
  }

  private setIsRareDiseaseValue(value: boolean): void {
    const isRareDiseaseControl = this.editForm.get(IS_RARE_DISEASE_FIELD_ID) as AbstractControl<boolean>;

    isRareDiseaseControl.patchValue(value);
  }

  // eslint-disable-next-line class-methods-use-this
  private dynamicFieldHasValue(dynamicFieldBlock: { [key: string]: string }[]): boolean {
    // one fieldBlock includes the subfields, e.g.: 'name, icd, id' of orphanet synonyms
    if (!dynamicFieldBlock.length) {
      return false;
    }

    return dynamicFieldBlock.some((subField) => {
      return Object.values(subField).some((v) => {
        return !!v;
      });
    });
  }
}
